import type { Status } from "@divespots/contracts";
import { pickLocale } from "@divespots/contracts";
import { useQuery } from "@tanstack/react-query";
import { Ban, FilePen, Plus, Search } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { api } from "../api/client.ts";
import { useLakes } from "../api/index.ts";
import { useLocale } from "../i18n/index.ts";
import { searchSpots } from "../search.ts";
import { Field } from "./ui.tsx";

type StatusFilter = Status | "all";

/** All spots including drafts (moderators see every non-deleted status). */
export function SpotsPage() {
  const { t } = useTranslation();
  const locale = useLocale();
  const lakes = useLakes();
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState<StatusFilter>("all");
  const [lake, setLake] = useState("");

  const q = useQuery({
    queryKey: ["admin", "spots"],
    queryFn: ({ signal }) => api.listSpots({}, signal).then((r) => r.spots),
  });

  const rows = useMemo(() => {
    let spots = q.data ?? [];
    if (status !== "all") spots = spots.filter((s) => s.status === status);
    if (lake) spots = spots.filter((s) => s.lakeSlug === lake);
    if (query.trim()) {
      const hits = new Set(
        searchSpots(spots, query, locale, lakes.data ?? [], 500).map((s) => s.id),
      );
      spots = spots.filter((s) => hits.has(s.id));
    }
    // drafts first, then by name
    return [...spots].sort(
      (a, b) =>
        Number(b.status === "draft") - Number(a.status === "draft") || a.name.localeCompare(b.name),
    );
  }, [q.data, status, lake, query, locale, lakes.data]);

  const drafts = (q.data ?? []).filter((s) => s.status === "draft").length;

  return (
    <div className="grid gap-6">
      <div className="adm-pagehead">
        <h1 className="ds-text-title-1 m-0">{t("admin.spots.title")}</h1>
        <Link className="ds-btn ds-btn--filled" to={`/${locale}/admin/spots/new`}>
          <Plus aria-hidden="true" strokeWidth={1.75} />
          {t("admin.spots.new")}
        </Link>
      </div>

      <div className="adm-filters" role="search" aria-label={t("admin.spots.filters")}>
        <Field label={t("admin.spots.search")}>
          {({ id }) => (
            <div className="adm-searchbox">
              <Search aria-hidden="true" strokeWidth={1.75} />
              <input
                id={id}
                type="search"
                className="adm-input"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
            </div>
          )}
        </Field>
        <Field label={t("admin.field.status")}>
          {({ id }) => (
            <select
              id={id}
              className="adm-input"
              value={status}
              onChange={(e) => setStatus(e.target.value as StatusFilter)}
            >
              <option value="all">{t("admin.status.all")}</option>
              <option value="draft">{t("admin.status.draft")}</option>
              <option value="published">{t("admin.status.published")}</option>
              <option value="pending">{t("admin.status.pending")}</option>
            </select>
          )}
        </Field>
        <Field label={t("lakeFilter.label")}>
          {({ id }) => (
            <select
              id={id}
              className="adm-input"
              value={lake}
              onChange={(e) => setLake(e.target.value)}
            >
              <option value="">{t("lakeFilter.all")}</option>
              {(lakes.data ?? []).map((l) => (
                <option key={l.slug} value={l.slug}>
                  {pickLocale(l.name, locale) ?? l.slug}
                </option>
              ))}
            </select>
          )}
        </Field>
      </div>

      {q.isPending ? <p role="status">{t("common.loading")}</p> : null}
      {q.isError ? (
        <p className="adm-error" role="alert">
          {t("admin.err.load")}
        </p>
      ) : null}
      {q.isSuccess ? (
        <p className="ds-text-footnote text-label-secondary m-0" role="status">
          {t("admin.spots.count", { count: rows.length })}
          {drafts > 0 ? ` · ${t("admin.spots.drafts", { count: drafts })}` : ""}
        </p>
      ) : null}

      {rows.length > 0 ? (
        <ul className="ds-list adm-list" aria-label={t("admin.spots.title")}>
          {rows.map((s) => (
            <li key={s.id} className="adm-spotrow" data-status={s.status}>
              <Link
                className="ds-row adm-spotrow__link"
                to={`/${locale}/admin/spots/${s.id}`}
                aria-label={t("admin.spots.open", { name: s.name })}
              >
                <span className="ds-row__icon bg-ocean-tint text-ocean-on-tint" aria-hidden="true">
                  <FilePen />
                </span>
                <span className="ds-row__title">
                  <span className="ds-text-headline">{s.name}</span>
                  <span className="ds-text-subheadline text-label-secondary block">
                    {[s.locality, s.waterBody].filter(Boolean).join(" · ")}
                  </span>
                </span>
                {s.permanentClosure ? (
                  <span className="ds-badge ds-badge--closed">
                    <Ban aria-hidden="true" strokeWidth={1.75} />
                    {t("admin.permanent.marker")}
                  </span>
                ) : null}
                <span
                  className={`ds-badge${s.status === "draft" ? " ds-badge--closed" : s.status === "published" ? " ds-badge--object" : ""}`}
                >
                  {t(`admin.status.${s.status}`)}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      ) : q.isSuccess ? (
        <p className="ds-text-subheadline text-label-secondary m-0">{t("admin.spots.empty")}</p>
      ) : null}
    </div>
  );
}
